import React from "react";
import { Box } from "@mui/material";
import { ContextMenuStore } from "../../../librettoEditor/store/ContextMenuStore";
import type { ContextMenuOption } from "../../../librettoEditor/components/ContextMenu/ContextMenu";

export type SceneItem = {
  id: string;
  thumbnailUrl: string;
  label?: string | number; // e.g., 1, 2, 3...
  meta?: Record<string, any>;
};

export type SceneMarkerItem = {
  id: string;
  title: string;
  seekTimeSec: number;
};

export interface SceneBarProps {
  scenes: SceneItem[];
  /**
   * Aspect ratio for scene thumbnails, e.g. `"16 / 9"`, `"9 / 16"`, `"1 / 1"`.
   * Defaults to `"16 / 9"`.
   */
  thumbnailAspectRatio?: string | number;
  selectedSceneId?: string | null;
  onSelect?: (sceneId: string) => void;
  onMarkerSelect?: (marker: SceneMarkerItem, scene: SceneItem) => void;
  buildContextMenu?: (scene: SceneItem) => ContextMenuOption[];
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Vertical scene thumbnail bar (left of transcript).
 * - Renders scene thumbnails top-to-bottom with spacing.
 * - Click to select; right-click opens context menu via ContextMenuStore.
 * - Pure presentational component; consumer wires selection and context actions.
 */
const SceneBar: React.FC<SceneBarProps> = ({
  scenes,
  thumbnailAspectRatio,
  selectedSceneId,
  onSelect,
  onMarkerSelect,
  buildContextMenu,
  className,
  style,
}) => {
  const resolvedAspectRatio = thumbnailAspectRatio ?? "16 / 9";

  const handleContextMenu = (
    e: React.MouseEvent,
    scene: SceneItem
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const options =
      buildContextMenu?.(scene) ??
      [
        { title: "Open", onClick: () => onSelect?.(scene.id) },
        { title: "Duplicate", onClick: () => {} },
        { title: "Delete", onClick: () => {} },
      ];
    ContextMenuStore.show(e.clientX, e.clientY, options);
  };

  return (
    <Box
      data-tour="scene-bar"
      className={className}
      style={style}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflowY: "auto",
        paddingY: 1,
        paddingX: 1,
        height: "100%",
        backgroundColor: "var(--app-bg-surface)",
          // On hover show a subtle shadow to indicate interactivity
            "&:hover": {
                boxShadow: "0 0 10px rgba(0, 0, 0, 0.08)",
                borderRight: "2px solid var(--app-border-subtle)",
            },
          borderRight: "2px solid transparent",
      }}
    >
      {scenes.map((scene, idx) => {
        const isSelected = scene.id === selectedSceneId;
        const markers: SceneMarkerItem[] = Array.isArray(scene.meta?.markers)
          ? (scene.meta?.markers as SceneMarkerItem[])
          : [];
        return (
          <Box
            key={scene.id}
            data-tour={idx === 0 ? "scene-thumbnail" : undefined}
            onClick={() => onSelect?.(scene.id)}
            onContextMenu={(e) => handleContextMenu(e, scene)}
            sx={{
              display: "block",
              cursor: "pointer",
              paddingY: 0.5,
              paddingX: 0.5,
              borderRadius: "10px",
              marginBottom: 1.25,
              transition: "background-color 120ms ease",
              backgroundColor: isSelected ? "rgba(43, 107, 253, 0.16)" : "transparent",
              "&:hover": { backgroundColor: isSelected ? "rgba(43, 107, 253, 0.22)" : "var(--app-bg-subtle)" },
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: resolvedAspectRatio,
                borderRadius: "10px",
                overflow: "hidden",
                border: isSelected ? "2px solid #2B6BFD" : "1px solid var(--app-border-subtle)",
                backgroundColor: "var(--app-bg-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {scene.thumbnailUrl ? (
                <img
                  src={scene.thumbnailUrl}
                  alt={`Scene ${scene.label ?? idx + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  onError={(e) => {
                    // Hide broken image, show placeholder
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                    color: "var(--app-text-muted)",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {scene.label ?? idx + 1}
                </Box>
              )}
            </Box>

            {markers.length > 0 ? (
              <Box
                sx={{
                  marginTop: 0.6,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                }}
              >
                {markers.map((m) => (
                  <Box
                    key={m.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onMarkerSelect?.(m, scene);
                    }}
                    title={m.title}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      paddingX: 0.4,
                      paddingY: 0.25,
                      borderRadius: "8px",
                      cursor: "pointer",
                      color: "var(--app-text-primary)",
                      fontSize: "12px",
                      lineHeight: "16px",
                      "&:hover": { backgroundColor: "rgba(99, 102, 241, 0.10)" },
                    }}
                  >
                    <Box component="span" sx={{ color: "var(--app-text-muted)", fontWeight: 700 }}>
                      #
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m.title}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

export default SceneBar;
